import { useState, useCallback, memo, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  RequestFormalProposalButton,
  AllAltQuoteOffers,
  AnonAccountModal,
} from "domains/altQuotes/components";
import { useInternalAltQuoteFunctionality } from "domains/altQuotes";
import { ILiquidityRequest } from "domains/liquidity/shared/types";
import { IAltQuoteOffers } from "domains/altQuotes/shared/types";
import { useClientsSelector } from "domains/clients/store/clients.selectors";
import { ApplyForLiquidityModal } from "domains/dashboard/components";
import { setCurrentAltQuote } from "domains/altQuotes/store/altQuotes.reducer";
import { RelationshipTypes } from "domains/clients/shared/types";
import { useTrustbenSelector } from "domains/trustben";
import { SecurityDisclaimer } from "domains/trustben/components";
import { liquidityApiReducer } from "domains/liquidity/store/liquidity.api.reducer";
import { useModalProps } from "common/shared-hooks/useModalProps";
import { AssetType } from "domains/liquidity/shared/constants";

import "./AltQuote.scss";

interface IProps {
  altQuoteOffers: IAltQuoteOffers;
  isSingleFundView: boolean;
  LRId: string;
  liquidityRequest: ILiquidityRequest | null;
  isOnlyNonFundAssetsSubmitted: boolean;
  isMixOfAssetsSubmitted: boolean;
  isFormalProposalRequested?: boolean;
}

const AltQuote = memo(function AltQuote({
  altQuoteOffers,
  isSingleFundView,
  LRId,
  liquidityRequest,
  isOnlyNonFundAssetsSubmitted,
  isMixOfAssetsSubmitted,
  isFormalProposalRequested,
}: IProps) {
  const { t } = useTranslation("altQuotes");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const selectAccountsResult = useMemo(() => liquidityApiReducer.endpoints.fetchLRAccounts.select(LRId), [ LRId ]);
  const { data: accounts } = useSelector(selectAccountsResult);

  const { isInternalAltQuoteFunctionality, isInternalAltQuote } = useInternalAltQuoteFunctionality();
  const { isTrustbenAltQuote } = useTrustbenSelector();
  const { isAdvisor } = useClientsSelector();
  const applyForLiquidityModalProps = useModalProps();
  const anonAccountModalProps = useModalProps();

  const [ isViewByOffer, setIsViewByOffer ] = useState(true);
  const [ isConvertingAdvisorAltQuoteWithoutClient, setIsAdvisorWithoutClients ] = useState(false);

  const isAltQuoteOffersReceived = !!altQuoteOffers.alt_quote_id;
  
    const selectAssetsResult = useMemo(() => liquidityApiReducer.endpoints.fetchLRAssets.select(LRId), [ LRId ]);
  const { data: assets = [] } = useSelector(selectAssetsResult);

  // Get non-fund assets for the message
  const nonFundAssets = useMemo(
    () => assets?.filter((asset) => asset.asset_type !== AssetType.Fund && asset.asset_type !== AssetType.PreqinFund),
    [ assets ],
  );

  // Format the asset info for the message
  const nonFundAssetsInfo = nonFundAssets?.map((asset) => asset.asset_name || asset.asset_type).join(', ') || 'N/A';

  const onRequestFormalProposal = useCallback(
    async (LRId: string) => {
      const client = accounts?.find(
        (account) => account.is_active && account.relationship_type === RelationshipTypes.Client,
      );
      if (isAdvisor && !client) {
        applyForLiquidityModalProps.handleShow();
        setIsAdvisorWithoutClients(true);
        dispatch(setCurrentAltQuote(liquidityRequest));
        return;
      }
      
      sessionStorage.setItem("LIQUIDITY_REQUEST_ID", LRId);

      if (isTrustbenAltQuote) {
        sessionStorage.setItem("REQUESTED_FORMAL_PROPOSAL", "true");
        anonAccountModalProps.handleShow();
      } else {
        // Navigate immediately with altQuote ID - let newLiquidityRequest handle the fetch
        const reasonParam = liquidityRequest?.reason_for_seeking_liquidity ? `&reason=${ liquidityRequest.reason_for_seeking_liquidity }` : '';
        navigate(`/my-applications/new-liquidity-request?confirmAssets=true${ reasonParam }`, { 
          state: { fromProposal: true, altQuoteId: LRId },
        });
      }
    },
    [ LRId, isAdvisor, liquidityRequest, accounts?.length, navigate ],
  );

  const openRequestFormalProposalModal = useCallback((liquidityRequest: ILiquidityRequest) => {
    // Navigate immediately with altQuote ID - let newLiquidityRequest handle the fetch
    sessionStorage.setItem("LIQUIDITY_REQUEST_ID", liquidityRequest.id);
    const reasonParam = liquidityRequest.reason_for_seeking_liquidity ? `&reason=${ liquidityRequest.reason_for_seeking_liquidity }` : '';
    navigate(`/my-applications/new-liquidity-request?confirmAssets=true${ reasonParam }`, { 
      state: { fromProposal: true, altQuoteId: liquidityRequest.id },
    });
  }, [ navigate ]);

  return (
    <>
      <div className="ben-altquote-page-wrapper ben-pb-3 ben-mb-3">
        <div className="ben-altquote-page-container">
          <div className={ `ben-altquote-page-title-container ${ isSingleFundView ? "ben-single-fund-view" : "" }` }>
            <h2>{t("My AltQuote")}</h2>
            {!isAltQuoteOffersReceived && !isOnlyNonFundAssetsSubmitted && (
              <p className="ben-mb-2">{t("AltQuote Title Text")}</p>
            )}
            {(isMixOfAssetsSubmitted) && (
              <p className="ben-mt-2 ben-altquote-page-bottom-info-message">
                We can only give an AltQuote on Fund assets at this time. 
                We are unable to provide an AltQuote on your Other Assets submission of: {nonFundAssetsInfo}. 
                Someone from the Ben team will reach out by email to discuss options.
              </p>
            )}
          </div>
          {!isOnlyNonFundAssetsSubmitted && (
            <div
              className={ `ben-mt-1 ben-altquote-page-offers-container ${
                isSingleFundView ? "ben-single-fund-view" : ""
              }` }
            >
              <div
                className={ `ben-altquote-page-assets-button ${ isViewByOffer ? "ben-active" : "" }` }
                onClick={ () => setIsViewByOffer(true) }
              >
                {t("View By Quote")}
              </div>
              <div
                className={ `ben-altquote-page-assets-button ${ isViewByOffer ? "" : "ben-active" }` }
                onClick={ () => setIsViewByOffer(false) }
              >
                {t("By Fund")}
              </div>
            </div>
          )}
        </div>
      </div>
      {!isOnlyNonFundAssetsSubmitted && (
        <AllAltQuoteOffers
          isViewByOffer={ isViewByOffer }
          viewByOfferDataSource={ altQuoteOffers.offers }
          viewByFundDataSource={ altQuoteOffers.fundOffers }
          liquidityRequest={ liquidityRequest }
        />
      )}
      {!isInternalAltQuoteFunctionality && !isOnlyNonFundAssetsSubmitted && (
        <p className="ben-mt-2 ben-altquote-page-bottom-info-message">{t("AltQuote Page.Bottom Info Text")}</p>
      )}
      {(isOnlyNonFundAssetsSubmitted) && (
        <p className="ben-mt-2 ben-altquote-page-bottom-info-message">
            We are unable to provide an AltQuote on your Other Assets submission of: {nonFundAssetsInfo}. 
            Someone from the Ben team will reach out by email to discuss options.
        </p>
      )}

      {isTrustbenAltQuote && !isOnlyNonFundAssetsSubmitted &&(
        <p className="ben-mt-2 ben-altquote-page-bottom-info-message">
          {"To request a formal proposal using this AltQuote, please click the button below and we will be in touch by email shortly with any additional information requests needed to provide the proposal."}
        </p>
      )}

      {!isInternalAltQuote && !isOnlyNonFundAssetsSubmitted &&(
        <RequestFormalProposalButton 
          onRequestFormalProposal={ onRequestFormalProposal } 
          LRId={ LRId }
          displayId={ liquidityRequest?.display_id }
          altQuoteSubmissionEmail={ liquidityRequest?.alt_quote_submission_email }
        />
      )}

      <div style={ { marginTop: 24 } }>
        <SecurityDisclaimer />
      </div>

      <AnonAccountModal
        isVisible={ anonAccountModalProps.isVisible }
        handleCancel={ anonAccountModalProps.handleHide }
      />

      <ApplyForLiquidityModal
        isVisible={ applyForLiquidityModalProps.isVisible }
        handleCancel={ applyForLiquidityModalProps.handleHide }
        isLA={ true }
        isConvertingAdvisorAltQuoteWithoutClient={ isConvertingAdvisorAltQuoteWithoutClient }
        openRequestFormalProposalModal={ openRequestFormalProposalModal }
      />
    </>
  );
});

export default AltQuote;
